import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import apiClient from '@/utils/api';
import { ReportDataSettings } from './ReportDataSettings';
import { ReportRecommendations } from './ReportRecommendations';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Sparkles, Bot, Send, RefreshCw, Settings2, Lightbulb, Mail, Clock, Rocket, TrendingUp, TrendingDown, Minus, Zap, Download } from 'lucide-react';

const MODELS = [
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (recommended)' },
  { value: 'claude-opus-4-8', label: 'Claude Opus 4.8 (most capable)' },
  { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 (fastest)' },
];

const DAYS = [
  { value: 'mon', label: 'Monday' }, { value: 'tue', label: 'Tuesday' },
  { value: 'wed', label: 'Wednesday' }, { value: 'thu', label: 'Thursday' },
  { value: 'fri', label: 'Friday' }, { value: 'sat', label: 'Saturday' },
  { value: 'sun', label: 'Sunday' },
];

const SCOPE_LABEL = { daily: 'Daily', weekly: 'Weekly Deep-Dive', monthly: 'Monthly' };

const fmtVal = (d) => {
  if (d.current === null || d.current === undefined) return 'Not available';
  if (d.is_currency) return `₹${Number(d.current).toLocaleString('en-IN')}`;
  if (d.is_rate) return `${d.current}%`;
  return `${d.current}`;
};

const MomentumChips = ({ deltas, prevLabel }) => {
  if (!deltas || !deltas.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-3" data-testid="momentum-chips">
      {deltas.map((d) => {
        const up = d.direction === 'up';
        const down = d.direction === 'down';
        const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
        const color = up ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
          : down ? 'text-red-700 bg-red-50 border-red-200'
          : 'text-slate-600 bg-slate-50 border-slate-200';
        const pctText = d.is_rate
          ? (d.previous === null || d.previous === undefined || d.change === null ? '—' : `${d.change > 0 ? '+' : ''}${Number(d.change).toFixed(1)} pp`)
          : d.pct === null || d.pct === undefined
          ? (d.current ? 'new' : '—')
          : `${d.pct > 0 ? '+' : ''}${d.pct}%`;
        return (
          <div key={d.metric} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs ${color}`} data-testid={`momentum-${d.metric}`}>
            <span className="text-slate-500">{d.label}</span>
            <span className="font-semibold text-slate-900">{fmtVal(d)}</span>
            <Icon size={13} />
            <span className="font-medium">{pctText}</span>
          </div>
        );
      })}
      {prevLabel && <div className="flex items-center text-[10px] text-slate-400 px-1">vs {prevLabel}</div>}
    </div>
  );
};

const IMPACT_STYLE = {
  High: 'bg-emerald-100 text-emerald-700',
  Medium: 'bg-amber-100 text-amber-700',
  Low: 'bg-slate-100 text-slate-600',
};
const EFFORT_STYLE = {
  Quick: 'bg-blue-100 text-blue-700',
  Medium: 'bg-indigo-100 text-indigo-700',
  Large: 'bg-purple-100 text-purple-700',
};

const AIInsights = () => {
  const mountedRef = React.useRef(true);
  React.useEffect(() => () => { mountedRef.current = false; }, []);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [reports, setReports] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genScope, setGenScope] = useState('daily');
  const [recipientsText, setRecipientsText] = useState('');
  const [tab, setTab] = useState('reports');
  const [ideasDoc, setIdeasDoc] = useState(null);
  const [ideasLoading, setIdeasLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async (report) => {
    if (downloading) return;
    setDownloading(true);
    try {
      const response = await apiClient.get(`/ai-analytics/reports/${report.id}/pdf`, { responseType: 'blob', timeout: 120000 });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a'); anchor.href = url;
      anchor.download = `patricks-crm-${report.scope}-${report.period_label || report.id}.pdf`.replace(/[/:]/g, '-');
      document.body.appendChild(anchor); anchor.click(); anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error('Could not download the PDF. Please retry.'); }
    finally { setDownloading(false); }
  };

  const loadAll = async () => {
    try {
      setLoading(true);
      const [s, r] = await Promise.all([
        apiClient.get('/ai-analytics/settings'),
        apiClient.get('/ai-analytics/reports?limit=20'),
      ]);
      setSettings(s.data);
      setRecipientsText((s.data.email_recipients || []).join(', '));
      setGenScope(s.data.scope || 'daily');
      const rows = (r.data.reports || []).filter(x => x.status !== 'generating');
      setReports(rows);
      if (rows.length) setActive(rows[0]);
    } catch (e) {
      toast.error('Failed to load AI insights');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const saveSettings = async () => {
    try {
      setSavingSettings(true);
      const payload = {
        ...settings,
        email_recipients: recipientsText.split(',').map(s => s.trim()).filter(Boolean),
      };
      const res = await apiClient.put('/ai-analytics/settings', payload);
      setSettings(res.data);
      setRecipientsText((res.data.email_recipients || []).join(', '));
      toast.success('AI Insights settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const generateNow = async (sendEmail) => {
    try {
      setGenerating(true);
      const scopeName = SCOPE_LABEL[genScope] || 'Daily';
      toast.info(`Claude is preparing your ${scopeName} report… this can take up to a couple of minutes.`);
      const res = await apiClient.post('/ai-analytics/generate', { scope: genScope, send_email: !!sendEmail });
      const pendingId = res.data.id;
      setActive(res.data); // shows "generating" state
      // poll until complete/failed (max ~4 min)
      let done = null;
      for (let i = 0; i < 40; i++) {
        await new Promise(r => setTimeout(r, 6000));
        if (!mountedRef.current) return;
        const p = await apiClient.get(`/ai-analytics/reports/${pendingId}`);
        if (p.data.status === 'complete' || p.data.status === 'failed') { done = p.data; break; }
      }
      if (!mountedRef.current) return;
      if (done && done.status === 'complete') {
        setActive(done);
        setReports((prev) => [done, ...prev.filter(x => x.id !== done.id)]);
        toast.success(sendEmail ? 'Report generated & emailed' : 'Report generated');
      } else if (done && done.status === 'failed') {
        toast.error(`Generation failed: ${done.error || 'unknown error'}`);
        setActive(done);
      } else {
        toast.error('Still generating — check back in a moment (Refresh in History).');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Report generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const sendReport = async (id) => {
    try {
      const res = await apiClient.post(`/ai-analytics/reports/${id}/send`);
      if (res.data.ok) toast.success(`Emailed to ${res.data.recipients} recipient(s)`);
      else toast.error(res.data.detail || 'Email send failed (check recipients / Resend key)');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    }
  };

  const generateIdeas = async () => {
    try {
      setIdeasLoading(true);
      toast.info('Claude is brainstorming CRM improvements…');
      const res = await apiClient.post('/ai-analytics/feature-suggestions');
      const id = res.data.id;
      let done = null;
      for (let i = 0; i < 40; i++) {
        await new Promise(r => setTimeout(r, 6000));
        if (!mountedRef.current) return;
        const p = await apiClient.get(`/ai-analytics/feature-suggestions/${id}`);
        if (p.data.status === 'complete' || p.data.status === 'failed') { done = p.data; break; }
      }
      if (!mountedRef.current) return;
      if (done && done.status === 'complete') {
        setIdeasDoc(done);
        toast.success(`${(done.ideas || []).length} ideas generated`);
      } else if (done && done.status === 'failed') {
        toast.error(`Failed: ${done.error || 'unknown error'}`);
      } else {
        toast.error('Still thinking — try again in a moment.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate ideas');
    } finally {
      setIdeasLoading(false);
    }
  };

  const loadLatestIdeas = async () => {
    try {
      const res = await apiClient.get('/ai-analytics/feature-suggestions?limit=1');
      const latest = (res.data.suggestions || []).find(x => x.status === 'complete');
      if (latest) setIdeasDoc(latest);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { if (tab === 'ideas' && !ideasDoc) loadLatestIdeas(); }, [tab]);

  if (loading) {
    return <div className="p-8 text-center text-slate-500" data-testid="ai-insights-loading"><i className="fas fa-spinner fa-spin mr-2"></i>Loading AI Insights…</div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6" data-testid="ai-insights-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-sm">
            <Sparkles className="text-white" size={22} />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">AI Insights</h1>
            <p className="text-sm text-slate-500">Claude analyses your operations, writes reports & suggests improvements</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={genScope}
            onChange={(e) => setGenScope(e.target.value)}
            className="text-sm px-3 py-2 rounded-md border border-slate-300 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-300"
            data-testid="gen-scope-select"
          >
            <option value="daily">Today</option>
            <option value="weekly">Past 7 days (Deep-Dive)</option>
            <option value="monthly">This Month</option>
          </select>
          <Button onClick={() => generateNow(false)} disabled={generating} className="bg-indigo-600 hover:bg-indigo-700" data-testid="generate-now-btn">
            {generating ? <><i className="fas fa-spinner fa-spin mr-2"></i>Analysing…</> : <><Bot size={16} className="mr-2" />Generate Now</>}
          </Button>
          <Button variant="outline" onClick={() => setShowSettings(v => !v)} data-testid="toggle-settings-btn">
            <Settings2 size={16} className="mr-2" />Settings
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200">
        <button
          onClick={() => setTab('reports')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'reports' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-reports"
        ><TrendingUp size={15} className="inline mr-1.5" />Reports</button>
        <button
          onClick={() => setTab('ideas')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'ideas' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-ideas"
        ><Rocket size={15} className="inline mr-1.5" />Product Ideas</button>
      </div>

      {/* Settings panel */}
      {showSettings && settings && (
        <Card data-testid="ai-settings-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Settings2 size={18} />Automation Settings</CardTitle>
            <CardDescription className="text-xs">Schedule automatic reports and email them to your team.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex items-center justify-between p-3 rounded-lg border bg-slate-50">
              <div>
                <Label className="text-sm font-medium">Daily automatic report</Label>
                <p className="text-xs text-slate-500">Runs every day at the scheduled time</p>
              </div>
              <Switch checked={!!settings.enabled} onCheckedChange={(v) => setSettings({ ...settings, enabled: v })} data-testid="enabled-switch" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm">Model</Label>
                <select value={settings.model} onChange={(e) => setSettings({ ...settings, model: e.target.value })} className="w-full text-sm px-3 py-2 rounded-md border border-slate-300 bg-white" data-testid="model-select">
                  {MODELS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Daily report scope</Label>
                <select value={settings.scope} onChange={(e) => setSettings({ ...settings, scope: e.target.value })} className="w-full text-sm px-3 py-2 rounded-md border border-slate-300 bg-white" data-testid="scope-select">
                  <option value="daily">Daily (today's activity)</option>
                  <option value="weekly">Weekly deep-dive (past 7 days)</option>
                  <option value="monthly">Monthly (month to date)</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm flex items-center gap-1.5"><Clock size={13} />Daily time (IST)</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" min={0} max={23} value={settings.schedule_hour} onChange={(e) => setSettings({ ...settings, schedule_hour: parseInt(e.target.value || '0') })} className="w-20" data-testid="schedule-hour-input" />
                  <span className="text-slate-400">:</span>
                  <Input type="number" min={0} max={59} value={settings.schedule_minute} onChange={(e) => setSettings({ ...settings, schedule_minute: parseInt(e.target.value || '0') })} className="w-20" data-testid="schedule-minute-input" />
                </div>
              </div>
            </div>

            {/* Weekly deep-dive automation */}
            <div className="rounded-lg border p-3 space-y-3 bg-violet-50/40 border-violet-200">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium flex items-center gap-1.5"><Rocket size={14} className="text-violet-600" />Weekly deep-dive report</Label>
                  <p className="text-xs text-slate-500">A richer COO-style review of the past 7 days, on your chosen day</p>
                </div>
                <Switch checked={!!settings.weekly_enabled} onCheckedChange={(v) => setSettings({ ...settings, weekly_enabled: v })} data-testid="weekly-enabled-switch" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm">Day</Label>
                  <select value={settings.weekly_day || 'mon'} onChange={(e) => setSettings({ ...settings, weekly_day: e.target.value })} className="w-full text-sm px-3 py-2 rounded-md border border-slate-300 bg-white" data-testid="weekly-day-select">
                    {DAYS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm flex items-center gap-1.5"><Clock size={13} />Time (IST)</Label>
                  <div className="flex items-center gap-2">
                    <Input type="number" min={0} max={23} value={settings.weekly_hour ?? 9} onChange={(e) => setSettings({ ...settings, weekly_hour: parseInt(e.target.value || '0') })} className="w-20" data-testid="weekly-hour-input" />
                    <span className="text-slate-400">:</span>
                    <Input type="number" min={0} max={59} value={settings.weekly_minute ?? 0} onChange={(e) => setSettings({ ...settings, weekly_minute: parseInt(e.target.value || '0') })} className="w-20" data-testid="weekly-minute-input" />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg border bg-slate-50">
              <div>
                <Label className="text-sm font-medium flex items-center gap-1.5"><Mail size={14} />Email the report</Label>
                <p className="text-xs text-slate-500">Sends the report to the addresses below (via Resend)</p>
              </div>
              <Switch checked={!!settings.send_email} onCheckedChange={(v) => setSettings({ ...settings, send_email: v })} data-testid="send-email-switch" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Recipient emails (comma-separated)</Label>
              <Input value={recipientsText} onChange={(e) => setRecipientsText(e.target.value)} placeholder="ops@patrickseducare.com, admin@patrickseducare.com" data-testid="recipients-input" />
              <p className="text-[11px] text-slate-400">Add as many as you like. In Resend test mode, emails only reach your own verified address until you verify a sending domain.</p>
            </div>

            <ReportDataSettings settings={settings} onChange={setSettings} />
            <div className="flex justify-end">
              <Button onClick={saveSettings} disabled={savingSettings} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-settings-btn">
                {savingSettings ? <><i className="fas fa-spinner fa-spin mr-2"></i>Saving…</> : 'Save Settings'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {tab === 'reports' && !active && (
        <Card>
          <CardContent className="py-16 text-center text-slate-500">
            <Bot size={40} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm">No reports yet. Click <strong>Generate Now</strong> to get your first AI analysis.</p>
          </CardContent>
        </Card>
      )}

      {tab === 'reports' && active && active.status === 'generating' && (
        <Card data-testid="report-generating-card">
          <CardContent className="py-16 text-center text-slate-500">
            <i className="fas fa-spinner fa-spin text-3xl text-indigo-500 mb-4"></i>
            <p className="text-sm font-medium text-slate-700">Claude is preparing your {SCOPE_LABEL[active.scope] || 'Daily'} report…</p>
            <p className="text-xs text-slate-400 mt-1">This can take up to a couple of minutes — the page will update automatically.</p>
          </CardContent>
        </Card>
      )}

      {tab === 'reports' && active && active.status === 'failed' && (
        <Card className="border-red-200" data-testid="report-failed-card">
          <CardContent className="py-12 text-center text-red-600">
            <i className="fas fa-triangle-exclamation text-2xl mb-3"></i>
            <p className="text-sm font-medium">Generation failed</p>
            <p className="text-xs text-red-400 mt-1">{active.error || 'Please try again.'}</p>
          </CardContent>
        </Card>
      )}

      {tab === 'reports' && active && active.status !== 'generating' && active.status !== 'failed' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main report */}
          <div className="lg:col-span-2 space-y-4">
            {/* Summary */}
            <Card className="border-l-4 border-l-indigo-500" data-testid="report-summary-card">
              <CardHeader className="pb-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle className="text-base flex items-center gap-2"><Mail size={18} className="text-indigo-600" />{active.report_version ? 'Daily Management Report' : 'Summary'}</CardTitle>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">{SCOPE_LABEL[active.scope] || 'Daily'}</Badge>
                    <Button size="sm" variant="outline" onClick={() => sendReport(active.id)} data-testid="send-report-btn"><Send size={14} className="mr-1.5" />Email</Button>
                    <Button size="sm" variant="outline" disabled={downloading} onClick={() => downloadPdf(active)} data-testid="download-report-pdf-btn"><Download size={14} className="mr-1.5" />{downloading ? 'Preparing…' : 'Download PDF'}</Button>
                  </div>
                </div>
                <CardDescription className="text-xs">{active.period_label} · {active.model}</CardDescription>
                {active.source_label && <p className="text-xs text-amber-800" data-testid="report-source-label">{active.source_label}</p>}
              </CardHeader>
              <CardContent>
                <MomentumChips deltas={active.snapshot?.comparison?.deltas} prevLabel={active.snapshot?.comparison?.previous_period_label} />
                {!active.report_version && <div className="whitespace-pre-wrap text-sm text-slate-800 bg-slate-50 rounded-lg p-4 leading-relaxed" data-testid="report-summary-text">{active.summary}</div>}
              </CardContent>
            </Card>
            {active.report_version && <details className="border-t py-3" data-testid="report-data-details"><summary className="text-sm text-slate-600 cursor-pointer min-h-[44px] flex items-center" data-testid="report-data-details-toggle">Data checks & metric definitions</summary>
              <ul className="list-disc pl-5 space-y-2 text-sm text-slate-600">{(active.snapshot?.data_quality || []).map((gap, i) => <li key={gap} data-testid={`report-data-gap-${i}`}>{gap}</li>)}</ul>
              <dl className="space-y-2 mt-4 text-xs text-slate-600">{Object.entries(active.snapshot?.definitions || {}).map(([key, value]) => <div key={key} data-testid={`report-definition-${key}`}><dt className="font-semibold">{key.replace(/_/g, ' ')}</dt><dd className="break-words">{Array.isArray(value) ? value.join(', ') : String(value)}</dd></div>)}</dl>
              {active.snapshot?.source_freshness && <p className="mt-3 text-xs text-slate-600 break-words" data-testid="report-source-freshness">Call-source markers: {Object.entries(active.snapshot.source_freshness).map(([key, value]) => `${key}: ${value || 'not recorded'}`).join(' · ') || 'Not recorded'}</p>}
              {active.snapshot?.reconciliation && <p className="mt-3 text-xs text-slate-600" data-testid="report-team-reconciliation">Team unique attempts: {active.snapshot.reconciliation.team_unique_attempted}; sum of staff unique attempts: {active.snapshot.reconciliation.sum_staff_unique_attempted}; unattributed records: {active.snapshot.reconciliation.unattributed_call_records}. {active.snapshot.reconciliation.note}</p>}
            </details>}

            {/* Suggestions */}
            {!active.report_version && (active.suggestions || []).length > 0 && (
              <Card data-testid="report-suggestions-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2"><Lightbulb size={18} className="text-amber-500" />Improvement Suggestions</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {active.suggestions.map((s, i) => (
                      <li key={i} className="flex gap-3 text-sm text-slate-700">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-semibold">{i + 1}</span>
                        <span className="pt-0.5">{s}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Full report */}
            <ReportRecommendations report={active} onUpdated={setActive} />
            <Card data-testid="report-full-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2"><Sparkles size={18} className="text-violet-600" />Full Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none prose-headings:text-slate-900 prose-table:text-sm prose-table:border prose-th:border prose-th:bg-slate-100 prose-th:px-3 prose-th:py-2 prose-td:border prose-td:px-3 prose-td:py-2 text-slate-700 overflow-x-auto" data-testid="report-markdown">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{active.report_markdown || ''}</ReactMarkdown>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* History */}
          <div>
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2"><Clock size={18} />History</CardTitle>
                  <Button size="sm" variant="ghost" onClick={loadAll} className="h-11 w-11 p-0" aria-label="Refresh report history" data-testid="refresh-report-history"><RefreshCw size={15} /></Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {reports.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => setActive(r)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${active?.id === r.id ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'}`}
                    data-testid={`history-item-${r.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-800">{r.period_label}</span>
                      <Badge className="text-[10px]" variant="outline">{r.scope}</Badge>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5 truncate">{(r.summary || '').replace(/\n/g, ' ').slice(0, 70)}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{new Date(r.created_at).toLocaleString()}</p>
                  </button>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* PRODUCT IDEAS TAB */}
      {tab === 'ideas' && (
        <div className="space-y-5" data-testid="ideas-panel">
          <Card className="bg-gradient-to-br from-violet-50 to-indigo-50 border-violet-200">
            <CardContent className="py-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center flex-shrink-0">
                  <Rocket className="text-white" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Ask Claude what to build next</h3>
                  <p className="text-sm text-slate-600 mt-0.5">Claude reviews your CRM and current usage, then proposes prioritised feature ideas to grow the business.</p>
                </div>
              </div>
              <Button onClick={generateIdeas} disabled={ideasLoading} className="bg-violet-600 hover:bg-violet-700 flex-shrink-0" data-testid="generate-ideas-btn">
                {ideasLoading ? <><i className="fas fa-spinner fa-spin mr-2"></i>Thinking…</> : <><Zap size={16} className="mr-2" />Suggest Improvements</>}
              </Button>
            </CardContent>
          </Card>

          {ideasLoading && !ideasDoc && (
            <Card><CardContent className="py-16 text-center text-slate-500">
              <i className="fas fa-spinner fa-spin text-3xl text-violet-500 mb-4"></i>
              <p className="text-sm">Claude is brainstorming improvements for your CRM…</p>
            </CardContent></Card>
          )}

          {!ideasLoading && !ideasDoc && (
            <Card><CardContent className="py-16 text-center text-slate-500">
              <Lightbulb size={40} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm">Click <strong>Suggest Improvements</strong> to get Claude's product recommendations.</p>
            </CardContent></Card>
          )}

          {ideasDoc && (ideasDoc.ideas || []).length > 0 && (
            <>
              <p className="text-xs text-slate-400">Generated {new Date(ideasDoc.created_at).toLocaleString()} · {ideasDoc.model}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ideasDoc.ideas.map((idea, i) => (
                  <Card key={i} className="hover:shadow-md transition-shadow" data-testid={`idea-card-${i}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-sm font-semibold text-slate-900 leading-snug">{idea.title}</CardTitle>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 pt-1">
                        <Badge className="text-[10px] bg-slate-100 text-slate-600">{idea.category}</Badge>
                        <Badge className={`text-[10px] ${IMPACT_STYLE[idea.impact] || 'bg-slate-100 text-slate-600'}`}>{idea.impact} impact</Badge>
                        <Badge className={`text-[10px] ${EFFORT_STYLE[idea.effort] || 'bg-slate-100 text-slate-600'}`}>{idea.effort}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-sm text-slate-600 leading-relaxed">{idea.description}</p>
                      {idea.why_now && (
                        <p className="text-xs text-violet-600 mt-2 flex items-start gap-1.5"><Zap size={12} className="mt-0.5 flex-shrink-0" />{idea.why_now}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>
      )}
      <CooChatWidget />
    </div>
  );
};

// Floating COO Assistant chat (bottom-right) — Admin/OH ask the AI about the whole CRM.
const CooChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const scrollRef = React.useRef(null);
  const recogRef = React.useRef(null);

  const toggleMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Voice input is not supported in this browser'); return; }
    if (listening) { try { recogRef.current?.stop(); } catch (_) {} return; }
    const r = new SR();
    r.lang = 'en-IN'; r.interimResults = false; r.maxAlternatives = 1;
    r.onresult = (e) => setInput((prev) => (prev ? prev + ' ' : '') + e.results[0][0].transcript);
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recogRef.current = r;
    setListening(true);
    r.start();
  };

  const saveReport = async (content, question) => {
    try {
      await apiClient.post('/ai-analytics/chat/save-report', { content, question });
      toast.success('Saved to AI Reports');
    } catch (e) {
      toast.error('Could not save analysis');
    }
  };

  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setBusy(true);
    try {
      const res = await apiClient.post('/ai-analytics/chat', { message: text, session_id: sessionId });
      if (res.data?.session_id) setSessionId(res.data.session_id);
      setMessages((m) => [...m, { role: 'assistant', content: res.data?.reply || 'No response.' }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: '⚠️ The AI assistant is unavailable right now. Please try again.' }]);
    } finally {
      setBusy(false);
    }
  };

  const suggestions = [
    'Give me a COO snapshot of the business right now',
    'Which counsellors are underperforming this month and why?',
    'Where is revenue leaking and what should I fix first?',
  ];

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="coo-chat-open"
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 px-5 py-3 text-white shadow-xl hover:shadow-2xl transition-shadow"
        >
          <Bot size={18} />
          <span className="text-sm font-semibold">Ask AI COO</span>
        </button>
      )}
      {open && (
        <div
          data-testid="coo-chat-panel"
          className="fixed bottom-6 right-6 z-50 flex flex-col w-[92vw] max-w-[400px] h-[70vh] max-h-[560px] rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
            <div className="flex items-center gap-2">
              <Bot size={18} />
              <div>
                <p className="text-sm font-semibold leading-tight">AI COO Assistant</p>
                <p className="text-[10px] opacity-80 leading-tight">Analyses your live CRM</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="coo-chat-close" className="text-white/80 hover:text-white text-lg leading-none px-1">×</button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-50" data-testid="coo-chat-messages">
            {messages.length === 0 && (
              <div className="text-center text-slate-500 mt-4">
                <Sparkles className="mx-auto mb-2 text-violet-500" size={22} />
                <p className="text-sm font-medium text-slate-700">Ask me anything about the CRM</p>
                <p className="text-xs mt-1 mb-3">Leads, admissions, revenue, teams, incentives…</p>
                <div className="space-y-1.5">
                  {suggestions.map((s, i) => (
                    <button key={i} onClick={() => setInput(s)} data-testid={`coo-chat-suggestion-${i}`}
                      className="block w-full text-left text-xs bg-white border border-slate-200 rounded-lg px-3 py-2 hover:border-violet-300 hover:bg-violet-50 transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${m.role === 'user' ? 'bg-violet-600 text-white rounded-br-sm' : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm'}`} data-testid={`coo-chat-msg-${m.role}`}>
                  {m.role === 'assistant'
                    ? <>
                        <div className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-headings:my-1 prose-strong:text-slate-900"><ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown></div>
                        <button
                          onClick={() => saveReport(m.content, messages[i - 1]?.role === 'user' ? messages[i - 1].content : '')}
                          data-testid={`coo-chat-save-${i}`}
                          className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-violet-600 hover:text-violet-800"
                        >
                          <i className="fas fa-bookmark"></i> Save as report
                        </button>
                      </>
                    : m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-slate-500 flex items-center gap-2" data-testid="coo-chat-thinking">
                  <RefreshCw size={14} className="animate-spin" /> Analysing your CRM…
                </div>
              </div>
            )}
          </div>

          <div className="p-2.5 border-t border-slate-200 bg-white flex items-center gap-2">
            <Button
              onClick={toggleMic}
              variant="outline"
              className={`h-10 w-10 p-0 shrink-0 ${listening ? 'bg-red-50 border-red-300 text-red-600 animate-pulse' : ''}`}
              data-testid="coo-chat-mic"
              title="Speak your question"
            >
              <i className={`fas ${listening ? 'fa-stop' : 'fa-microphone'}`}></i>
            </Button>
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={listening ? 'Listening…' : 'Ask the AI COO…'}
              disabled={busy}
              className="h-10 text-sm"
              data-testid="coo-chat-input"
            />
            <Button onClick={send} disabled={busy || !input.trim()} className="h-10 px-3 bg-violet-600 hover:bg-violet-700" data-testid="coo-chat-send">
              <Send size={16} />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default AIInsights;
